import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FaX } from 'react-icons/fa6'
import { useSearchParams } from 'react-router-dom'
import Select from 'react-select'
import { z } from 'zod'

import { Button } from '../../../../components/Button'
import { Input } from '../../../../components/Input'
import { useAppSelector } from '../../../../hooks/useAppSelector'
import { usePermission } from '../../../../hooks/usePermission'
import { Role } from '../../../../interfaces/rolesEnum'
import { SelectOption } from '../../../../reducers/appReducer'
import { setCityByStateOptionsAction } from '../../../../services/appServices'
import { getAllPromotersCompanies } from '../../../../services/promotersCompaniesServices'
import { getUsersByRole } from '../../../../services/userServices'

interface FilterStoresFormModalProps {
  onClose: () => void
}

const filterStoresFormSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  state: z.string().optional(),
  city: z.string().optional(),
  seller_id: z.string().optional(),
  supervisor_id: z.string().optional(),
  promoter_id: z.string().optional(),
  promoters_company_id: z.string().optional(),
})

type FilterStoresFormData = z.infer<typeof filterStoresFormSchema>

export function FilterStoresFormModal({
  onClose,
}: FilterStoresFormModalProps) {
  const [sellerSelect, setSellerSelect] = useState<SelectOption[]>([])
  const [supervisorsSelect, setSupervisorsSelect] = useState<SelectOption[]>([])
  const [promoterSelect, setPromoterSelect] = useState<SelectOption[]>([])

  const { defaultFormData } = useAppSelector(state => state.app)

  const { updateStoreSupervisor, editStorePromotersCompany } = usePermission()

  const [searchParams, setSearchParams] = useSearchParams()

  const { register, handleSubmit, control, watch } = useForm<FilterStoresFormData>({
    resolver: zodResolver(filterStoresFormSchema),
    defaultValues: {
      id: searchParams.get('id') || '',
      name: searchParams.get('name') || '',
      state: searchParams.get('state') || '',
      city: searchParams.get('city') || '',
      seller_id: searchParams.get('seller_id') || '',
      supervisor_id: searchParams.get('supervisor_id') || '',
      promoter_id: searchParams.get('promoter_id') || '',
      promoters_company_id: searchParams.get('promoters_company_id') || '',
    },
  })

  const selectedState = watch('state')

  function handleFilterUsers(data: FilterStoresFormData) {
    setSearchParams(state => {
      if (data.id) {
        state.set('id', data.id)
      } else {
        state.delete('id')
      }
      if (data.name) {
        state.set('name', data.name)
      } else {
        state.delete('name')
      }
      if (data.state) {
        state.set('state', data.state)
      } else {
        state.delete('state')
      }
      if (data.city) {
        state.set('city', data.city)
      } else {
        state.delete('city')
      }
      if (data.seller_id) {
        state.set('seller_id', data.seller_id)
      } else {
        state.delete('seller_id')
      }
      if (data.supervisor_id) {
        state.set('supervisor_id', data.supervisor_id)
      } else {
        state.delete('supervisor_id')
      }
      if (data.promoter_id) {
        state.set('promoter_id', data.promoter_id)
      } else {
        state.delete('promoter_id')
      }
      if (data.promoters_company_id) {
        state.set('promoters_company_id', data.promoters_company_id)
      } else {
        state.delete('promoters_company_id')
      }

      state.set('page', '1')
      return state
    })

    onClose()
  }

  const defineSelects = useCallback(async () => {
    const [{ users: sellers }, { users: promoters }, { users: supervisors }] = await Promise.all([
      getUsersByRole(Role.SELLER),
      getUsersByRole(Role.PROMOTER),
      getUsersByRole(Role.SUPERVISOR),
    ])
    setSellerSelect(sellers.map(seller => ({ label: seller.name, value: seller.id })))
    setPromoterSelect(promoters.map(promoter => ({ label: promoter.name, value: promoter.id })))
    setSupervisorsSelect(supervisors.map(supervisor => ({ label: supervisor.name, value: supervisor.id })))
  }, [])

  const { data: promotersCompanySelect } = useQuery({
    queryKey: ['stores_promoters_company_select'],
    queryFn: async () => {
      const { promotersCompanies } = await getAllPromotersCompanies()
      return promotersCompanies.map(item => ({ value: item.id, label: item.name }))
    },
  })

  useEffect(() => {
    defineSelects()
  }, [defineSelects])

  useEffect(() => {
    if (selectedState) {
      setCityByStateOptionsAction(selectedState)
    }
  }, [selectedState])

  return (
    <div
      className="fixed top-0 left-0 w-screen h-screen bg-black/65 flex items-center justify-center z-[10000]"
      onClick={onClose}
    >
      <div
        className="flex flex-col bg-slate-100 relative rounded-md p-4 w-[80vw] max-h-[80dvh] md:w-96"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex justify-between mb-4">
          <h1 className="font-semibold">
            Filtros de lojas
          </h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <FaX />
          </Button>
        </header>
        <form
          className="flex flex-col space-y-4 w-full overflow-y-auto"
          onSubmit={handleSubmit(handleFilterUsers)}
        >
          <div>
            <Input
              placeholder="ID"
              id="id"
              label="Id da loja"
              {...register('id')}
            />
          </div>
          <div>
            <Input
              label="Nome"
              id="name"
              placeholder="Fulano de tal"
              {...register('name')}
            />
          </div>
          <div>
            <Controller
              control={control}
              name="state"
              render={({ field: { onChange, value } }) => (
                <>
                  <label
                    className="block text-sm/6 font-medium text-gray-900"
                    htmlFor="state"
                  >
                    Estado (UF)
                  </label>
                  <Select
                    id="state"
                    options={defaultFormData.stateSelect}
                    onChange={state => onChange(state?.value)}
                    value={defaultFormData.stateSelect.filter(state => state.value === value)}
                    placeholder="Estado..."
                  />
                </>
              )}
            />
          </div>
          <div>
            <Controller
              control={control}
              name="city"
              render={({ field: { onChange, value } }) => (
                <>
                  <label
                    className="block text-sm/6 font-medium text-gray-900"
                    htmlFor="city"
                  >
                    Cidade
                  </label>
                  <Select
                    id="state"
                    options={defaultFormData.citySelect}
                    onChange={state => onChange(state?.value)}
                    value={defaultFormData.citySelect.filter(state => state.value === value)}
                    placeholder={!selectedState
                      ? 'Selecione um estado'
                      : 'Cidade...'}
                    isDisabled={!selectedState}
                  />
                </>
              )}
            />
          </div>
          <div>
            <Controller
              name="seller_id"
              control={control}
              render={({ field: { onChange, value } }) => (
                <>
                  <label
                    className="block text-sm/6 font-medium text-gray-900"
                    htmlFor="seller_id"
                  >
                    Vendedor
                  </label>
                  <Select
                    id="seller_id"
                    options={sellerSelect}
                    onChange={state => onChange(state?.value)}
                    value={sellerSelect.filter(city => city.value === value)}
                    placeholder="Vendedor..."
                  />
                </>
              )}
            />
          </div>
          {updateStoreSupervisor && (
            <div>
              <Controller
                name="supervisor_id"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <>
                    <label
                      className="block text-sm/6 font-medium text-gray-900"
                      htmlFor="supervisor_id"
                    >
                      Supervisor
                    </label>
                    <Select
                      id="supervisor_id"
                      options={supervisorsSelect}
                      onChange={state => onChange(state?.value)}
                      value={supervisorsSelect.filter(city => city.value === value)}
                      placeholder="Supervisor..."
                    />
                  </>
                )}
              />
            </div>
          )}
          <div>
            <Controller
              name="promoter_id"
              control={control}
              render={({ field: { onChange, value } }) => (
                <>
                  <label
                    className="block text-sm/6 font-medium text-gray-900"
                    htmlFor="promoter_id"
                  >
                    Promotor
                  </label>
                  <Select
                    id="promoter_id"
                    options={promoterSelect}
                    onChange={state => onChange(state?.value)}
                    value={promoterSelect.filter(city => city.value === value)}
                    placeholder="Promotor..."
                  />
                </>
              )}
            />
          </div>
          {editStorePromotersCompany && (
            <div>
              <Controller
                control={control}
                name="promoters_company_id"
                render={({ field: { onChange, value } }) => (
                  <>
                    <label
                      className="block text-sm/6 font-medium text-gray-900 mb-1"
                      htmlFor="promoters_company_id"
                    >
                      Empresa de promotores
                      <span className="text-red-500 ml-2">
                        *
                      </span>
                    </label>
                    <Select
                      id="promoters_company_id"
                      options={promotersCompanySelect}
                      onChange={state => onChange(state?.value)}
                      value={promotersCompanySelect && promotersCompanySelect.filter(role => role.value === value)}
                      placeholder="Selecione..."
                    />
                  </>
                )}
              />
            </div>
          )}
          <footer className="ml-auto pt-4">
            <Button
              type="submit"
              size="sm"
            >
              Filtrar
            </Button>
          </footer>
        </form>
      </div>
    </div>
  )
}
